// src/components/common/ConfirmModal.tsx

import React from "react";
import ReactDOM from "react-dom";
import "./ConfirmModal.css";

interface ConfirmModalProps {
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
  // Optional extra content rendered between the message and the action buttons.
  // Used by admin/mod flows to inject a reason input field.
  children?: React.ReactNode;
}

/**
 * Lightweight confirm dialog rendered into a portal.
 * Replaces all inline "Emin misiniz?" confirmation rows so the UI stays
 * consistent across TrashPage, the entry row menu, and anywhere else.
 *
 * The optional children slot allows callers to inject extra form fields
 * (e.g. a reason textarea for admin/mod delete/edit actions) without
 * requiring a separate modal component.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  disabled = false,
  children,
}) => {
  return ReactDOM.createPortal(
    // Semi-transparent backdrop — clicking it cancels
    <div className="confirm-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
      >
        <p className="confirm-modal__message">{message}</p>

        {/* Optional extra content (e.g. reason input for admin/mod actions) */}
        {children && (
          <div className="confirm-modal__extra">
            {children}
          </div>
        )}

        <div className="confirm-modal__actions">
          <button
            className="confirm-modal__btn confirm-modal__btn--cancel"
            onClick={onCancel}
            disabled={disabled}
          >
            {cancelLabel}
          </button>
          <button
            className="confirm-modal__btn confirm-modal__btn--danger"
            onClick={onConfirm}
            disabled={disabled}
            // Auto-focus the destructive action so keyboard users can confirm quickly
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};